import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  api,
  type AgentJobItem,
  type ExecutiveMemoJobStatusData,
  type NewsJobMode,
  type NewsJobStatusData,
  type NewsNotificationType,
  type ReceiptJobStatusData,
} from "../lib/api";
import { storage } from "../lib/storage";
import { AppError, logError } from "../lib/errors";
import {
  JOB_NOTIFICATION_EVENT,
  getPendingJobNotificationKey,
  readPendingJobNotifications,
  removePendingJobNotification,
  type PendingJobNotification,
} from "../lib/jobNotifications";
import {
  clearPendingNewsJobSnapshot,
  formatNewsJobStageLabel,
  getNewsNotificationTypeLabel,
  readPendingNewsJobSnapshot,
  toPendingNewsJobNotification,
  writePendingNewsJobNotification,
} from "../lib/newsJob";
import {
  clearPendingReceiptJobSnapshot,
  formatReceiptStageLabel,
  getReceiptPollIntervalMs,
  readPendingReceiptJobSnapshot,
  writePendingReceiptJobNotification,
} from "../lib/receipt";
import {
  clearPendingExecutiveMemoJobSnapshot,
  formatExecutiveMemoStageLabel,
  getExecutiveMemoPollIntervalMs,
  readPendingExecutiveMemoJobSnapshot,
  toPendingExecutiveMemoJobNotification,
  writePendingExecutiveMemoJobNotification,
} from "../lib/executiveMemos";

type JobToastState = {
  key: string;
  tone: "success" | "error";
  title: string;
  detail?: string;
  linkTo?: string;
  notification: PendingJobNotification;
};

export function getJobFailureMessage(failedError?: string) {
  const message = (failedError ?? "").trim();
  if (!message) return "結果を確認できませんでした。";

  const lowered = message.toLowerCase();
  if (
    lowered.includes("valueerror") ||
    lowered.includes("did not include a json object") ||
    lowered.includes("gemini evidence bundle")
  ) {
    return "ニュース内容の生成に失敗しました。しばらくしてから再試行してください。";
  }

  return message;
}

export function formatCompactNotificationText(value?: string | null, maxLength = 42) {
  const normalized = (value ?? "").replace(/\s+/g, " ").trim();
  if (!normalized) return undefined;
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength)}...` : normalized;
}

function getNewsJobCompletionDetail(
  notification: Extract<PendingJobNotification, { kind: "news" }>,
  contentLabel: string
) {
  const destination = getNewsNotificationTypeLabel(notification.notificationType);
  const keyword = formatCompactNotificationText(notification.keyword);

  if (keyword) {
    return `「${keyword}」の${contentLabel}を${destination}へ送信しました。`;
  }

  return `${destination}への${contentLabel}送信が完了しました。`;
}

function buildJobToast(notification: PendingJobNotification): JobToastState {
  if (notification.kind === "level_up") {
    return {
      key: getPendingJobNotificationKey(notification),
      tone: "success",
      title: "ランクアップしました",
      detail: `親密度は${notification.rankName}になりました`,
      notification,
    };
  }

  if (notification.kind === "mode_unlock") {
    return {
      key: getPendingJobNotificationKey(notification),
      tone: "success",
      title: `${notification.modeName ?? "新しい会話モード"}が解放されました`,
      detail: "新しい会話モードを試してください。",
      notification,
    };
  }

  if (notification.kind === "receipt") {
    return {
      key: getPendingJobNotificationKey(notification),
      tone: notification.status === "completed" ? "success" : "error",
      title:
        notification.status === "completed"
          ? "領収書の処理が完了しました"
          : notification.status === "needs_input"
            ? "領収書の処理に追加情報が必要です"
          : "領収書の処理に失敗しました",
      detail:
        notification.status === "completed"
          ? notification.extractedSummary ??
            "Google Drive と Google スプレッドシートへの保存が完了しました。"
          : notification.status === "needs_input"
            ? notification.failedError ?? "内容を確認して、追加情報を入力してください。"
          : notification.failedStage
            ? `${getJobFailureMessage(notification.failedError)} (${formatReceiptStageLabel(
                notification.failedStage
              )})`
            : getJobFailureMessage(notification.failedError),
      linkTo: notification.status === "completed" ? "/receipt/history" : undefined,
      notification,
    };
  }

  if (notification.kind === "executive_memo") {
    if (notification.status === "processing") {
      return {
        key: getPendingJobNotificationKey(notification),
        tone: "success",
        title: "社長メモを処理中です",
        detail: "画像を解析してメモを整理しています。",
        notification,
      };
    }

    return {
      key: getPendingJobNotificationKey(notification),
      tone: notification.status === "completed" ? "success" : "error",
      title:
        notification.status === "completed"
          ? "社長メモを保存しました"
          : notification.status === "timed_out"
            ? "社長メモの処理がタイムアウトしました"
            : "社長メモの整理に失敗しました",
      detail:
        notification.status === "completed"
          ? notification.title ?? notification.summary ?? "社長メモの整理が完了しました。"
          : notification.failedStage
            ? `${getJobFailureMessage(notification.failedError)} (${formatExecutiveMemoStageLabel(
                notification.failedStage
              )})`
            : getJobFailureMessage(notification.failedError),
      notification,
    };
  }

  const contentLabel = notification.mode === "report" ? "レポート" : "ニュース";

  return {
    key: getPendingJobNotificationKey(notification),
    tone: notification.status === "completed" ? "success" : "error",
    title:
      notification.status === "completed"
        ? `${contentLabel}送信の処理が完了しました`
        : notification.status === "needs_input"
          ? `${contentLabel}送信に追加情報が必要です`
        : `${contentLabel}送信の処理に失敗しました`,
    detail:
      (() => {
        if (notification.status === "completed") {
          return getNewsJobCompletionDetail(notification, contentLabel);
        }

        if (notification.status === "needs_input") {
          return notification.failedError ?? "内容を確認して、追加情報を入力してください。";
        }

        if (notification.failedStage) {
          return `${getJobFailureMessage(notification.failedError)} (${formatNewsJobStageLabel(
            notification.failedStage
          )})`;
        }

        return getJobFailureMessage(notification.failedError);
      })(),
    notification,
  };
}

function isJobNotFoundError(error: unknown) {
  return error instanceof AppError && error.code === "NOT_FOUND";
}

function toPendingReceiptNotification(response: ReceiptJobStatusData) {
  if (response.status !== "completed" && response.status !== "failed") {
    return null;
  }

  return {
    jobId: response.jobId,
    status: response.status,
    processedAt: response.processedAt ?? undefined,
    extractedSummary: response.extractedSummary ?? undefined,
    failedStage: response.failedStage ?? undefined,
    failedError: response.failedError ?? undefined,
  } as const;
}

function normalizeJobString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function getAgentJobMeta(item: AgentJobItem): Record<string, unknown> | null {
  if (!item.meta) return null;
  if (isRecord(item.meta)) return item.meta;
  if (typeof item.meta !== "string") return null;

  try {
    const parsed = JSON.parse(item.meta);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function getAgentJobString(item: AgentJobItem, keys: string[]): string | undefined {
  const record = item as Record<string, unknown>;
  for (const key of keys) {
    const value = normalizeJobString(record[key]);
    if (value) return value;
  }
  return undefined;
}

function getAgentJobMetaString(item: AgentJobItem, keys: string[]): string | undefined {
  const meta = getAgentJobMeta(item);
  if (!meta) return undefined;
  for (const key of keys) {
    const value = normalizeJobString(meta[key]);
    if (value) return value;
  }
  return undefined;
}

function getCommonAgentJobId(item: AgentJobItem) {
  return getAgentJobString(item, ["jobId", "job_id", "id"]);
}

function getCommonAgentJobStatus(item: AgentJobItem) {
  const rawStatus = getAgentJobString(item, ["status", "rawStatus", "raw_status"]) ?? "";
  const normalized = rawStatus.trim().toLowerCase();
  if (normalized === "pending" || normalized === "requesting") return "accepted";
  if (normalized === "in_progress" || normalized === "runnning") return "running";
  if (normalized === "ambiguous" || normalized === "needs_more_info") return "needs_input";
  return normalized;
}

function getCommonAgentJobProcessedAt(item: AgentJobItem) {
  return getAgentJobString(item, ["processedAt", "processed_at", "updatedAt", "updated_at"]);
}

function getCommonAgentJobSummary(item: AgentJobItem) {
  return getAgentJobString(item, ["resultSummary", "result_summary", "summary"]);
}

function getCommonAgentJobError(item: AgentJobItem) {
  return getAgentJobString(item, ["errorMessage", "error_message"]);
}

function isCommonAgentJobPending(item: AgentJobItem) {
  const status = getCommonAgentJobStatus(item);
  return status === "accepted" || status === "running";
}

function toNotificationStatus(status: string) {
  if (status === "completed" || status === "failed" || status === "needs_input") {
    return status;
  }
  return null;
}

function normalizeNewsNotificationType(value: string | undefined): NewsNotificationType | undefined {
  if (value === "line" || value === "google_chat" || value === "email") {
    return value;
  }
  return undefined;
}

function normalizeNewsMode(value: string | undefined): NewsJobMode | undefined {
  if (value === "news" || value === "report") return value;
  return undefined;
}

function toPendingReceiptNotificationFromAgentJob(item: AgentJobItem) {
  const status = toNotificationStatus(getCommonAgentJobStatus(item));
  const jobId = getCommonAgentJobId(item);
  if (!status || !jobId) return null;

  const summary = getCommonAgentJobSummary(item);
  const error = getCommonAgentJobError(item) ?? summary;

  return {
    jobId,
    status,
    processedAt: getCommonAgentJobProcessedAt(item),
    extractedSummary: status === "completed" ? summary : undefined,
    failedStage: getAgentJobMetaString(item, ["failed_stage", "failedStage", "stage"]),
    failedError: status === "completed" ? undefined : error,
  } as const;
}

function toPendingNewsNotificationFromAgentJob(
  item: AgentJobItem,
  fallbackMode?: NewsJobMode | null,
  fallbackKeyword?: string | null
) {
  const status = toNotificationStatus(getCommonAgentJobStatus(item));
  const jobId = getCommonAgentJobId(item);
  if (!status || !jobId) return null;

  const summary = getCommonAgentJobSummary(item);
  const error = getCommonAgentJobError(item) ?? summary;
  const resource = getAgentJobString(item, ["resource"]);
  const metaMode = normalizeNewsMode(
    getAgentJobMetaString(item, ["mode", "news_mode", "newsMode"])
  );

  return {
    jobId,
    status,
    processedAt: getCommonAgentJobProcessedAt(item),
    keyword:
      formatCompactNotificationText(fallbackKeyword, 80) ??
      getAgentJobMetaString(item, ["keyword", "topic", "theme"]) ??
      formatCompactNotificationText(getAgentJobString(item, ["summary"]), 80) ??
      getAgentJobString(item, ["title"]),
    notificationType: normalizeNewsNotificationType(
      getAgentJobMetaString(item, ["notification_type", "notificationType"])
    ),
    mode: resource === "report" ? "report" : metaMode ?? fallbackMode ?? undefined,
    failedStage: getAgentJobMetaString(item, ["failed_stage", "failedStage", "stage"]),
    failedError: status === "completed" ? undefined : error,
    summary: status === "completed" ? summary : undefined,
  } as const;
}

async function fetchCommonAgentJob(resource: "news" | "report" | "receipt", jobId: string) {
  const getAgentJob = api.getAgentJob as unknown;
  if (typeof getAgentJob !== "function") {
    return null;
  }

  try {
    return await getAgentJob(resource, jobId) as AgentJobItem;
  } catch (error) {
    if (isJobNotFoundError(error)) {
      return null;
    }
    throw error;
  }
}

type PendingReceiptAgentJobNotification = NonNullable<
  ReturnType<typeof toPendingReceiptNotificationFromAgentJob>
>;
type PendingNewsAgentJobNotification = NonNullable<
  ReturnType<typeof toPendingNewsNotificationFromAgentJob>
>;

export function JobNotificationCenter() {
  const location = useLocation();
  const receiptPollTimerRef = useRef<number | null>(null);
  const executiveMemoPollTimerRef = useRef<number | null>(null);
  const newsPollTimerRef = useRef<number | null>(null);
  const dismissTimerRef = useRef<number | null>(null);
  const [notifications, setNotifications] = useState<PendingJobNotification[]>([]);

  const syncNotifications = useCallback(() => {
    setNotifications(readPendingJobNotifications());
  }, []);

  const dismissToast = useCallback((notification: PendingJobNotification) => {
    if (notification.kind === "mode_unlock") {
      storage.addAcknowledgedUnlockedMode(notification.characterId, notification.mode);
    }
    removePendingJobNotification(notification);
  }, []);

  const stopReceiptPolling = useCallback(() => {
    if (receiptPollTimerRef.current !== null) {
      window.clearTimeout(receiptPollTimerRef.current);
      receiptPollTimerRef.current = null;
    }
  }, []);

  const stopNewsPolling = useCallback(() => {
    if (newsPollTimerRef.current !== null) {
      window.clearTimeout(newsPollTimerRef.current);
      newsPollTimerRef.current = null;
    }
  }, []);

  const stopExecutiveMemoPolling = useCallback(() => {
    if (executiveMemoPollTimerRef.current !== null) {
      window.clearTimeout(executiveMemoPollTimerRef.current);
      executiveMemoPollTimerRef.current = null;
    }
  }, []);

  const handleResolvedReceiptJob = useCallback(
    (response: ReceiptJobStatusData) => {
      const notification = toPendingReceiptNotification(response);
      if (!notification) {
        return;
      }

      clearPendingReceiptJobSnapshot();
      stopReceiptPolling();
      writePendingReceiptJobNotification(notification);
    },
    [stopReceiptPolling]
  );

  const handleResolvedExecutiveMemoJob = useCallback(
    (response: ExecutiveMemoJobStatusData) => {
      const notification = toPendingExecutiveMemoJobNotification(response);
      if (!notification) {
        return;
      }

      clearPendingExecutiveMemoJobSnapshot();
      stopExecutiveMemoPolling();
      writePendingExecutiveMemoJobNotification(notification);
    },
    [stopExecutiveMemoPolling]
  );

  const handleResolvedNewsJob = useCallback(
    (response: NewsJobStatusData) => {
      const pendingSnapshot = readPendingNewsJobSnapshot();
      const notification = toPendingNewsJobNotification(response, {
        mode: pendingSnapshot?.mode,
      });
      if (!notification) {
        return;
      }

      clearPendingNewsJobSnapshot();
      stopNewsPolling();
      writePendingNewsJobNotification(notification);
    },
    [stopNewsPolling]
  );

  const handleResolvedReceiptAgentJob = useCallback(
    (notification: PendingReceiptAgentJobNotification) => {
      clearPendingReceiptJobSnapshot();
      stopReceiptPolling();
      writePendingReceiptJobNotification(notification);
    },
    [stopReceiptPolling]
  );

  const handleResolvedNewsAgentJob = useCallback(
    (notification: PendingNewsAgentJobNotification) => {
      clearPendingNewsJobSnapshot();
      stopNewsPolling();
      writePendingNewsJobNotification(notification);
    },
    [stopNewsPolling]
  );

  const scheduleReceiptJobPoll = useCallback(
    (jobId: string, startedAtMs: number, clientRequestId?: string) => {
      stopReceiptPolling();
      const pollIntervalMs = getReceiptPollIntervalMs(Math.max(0, Date.now() - startedAtMs));

      receiptPollTimerRef.current = window.setTimeout(async () => {
        try {
          const commonResponse = await fetchCommonAgentJob("receipt", jobId);
          if (commonResponse) {
            if (isCommonAgentJobPending(commonResponse)) {
              scheduleReceiptJobPoll(jobId, startedAtMs, clientRequestId);
              return;
            }

            const notification = toPendingReceiptNotificationFromAgentJob(commonResponse);
            if (notification) {
              handleResolvedReceiptAgentJob(notification);
              return;
            }
          }

          const response = await api.getReceiptJob(jobId);

          if (response.status === "accepted") {
            scheduleReceiptJobPoll(jobId, startedAtMs, response.clientRequestId ?? clientRequestId);
            return;
          }

          handleResolvedReceiptJob(response);
        } catch (error) {
          if (isJobNotFoundError(error)) {
            clearPendingReceiptJobSnapshot();
            stopReceiptPolling();
            return;
          }

          logError(error);
          scheduleReceiptJobPoll(jobId, startedAtMs, clientRequestId);
        }
      }, pollIntervalMs);
    },
    [handleResolvedReceiptAgentJob, handleResolvedReceiptJob, stopReceiptPolling]
  );

  const scheduleExecutiveMemoJobPoll = useCallback(
    (jobId: string, startedAtMs: number, clientRequestId?: string) => {
      stopExecutiveMemoPolling();
      const pollIntervalMs = getExecutiveMemoPollIntervalMs(Math.max(0, Date.now() - startedAtMs));

      executiveMemoPollTimerRef.current = window.setTimeout(async () => {
        try {
          const response = await api.getExecutiveMemoJob(jobId);

          if (response.relayStatus === "accepted" || response.relayStatus === "processing") {
            scheduleExecutiveMemoJobPoll(
              jobId,
              startedAtMs,
              response.clientRequestId ?? clientRequestId
            );
            return;
          }

          handleResolvedExecutiveMemoJob(response);
        } catch (error) {
          if (isJobNotFoundError(error)) {
            clearPendingExecutiveMemoJobSnapshot();
            stopExecutiveMemoPolling();
            return;
          }

          logError(error);
          scheduleExecutiveMemoJobPoll(jobId, startedAtMs, clientRequestId);
        }
      }, pollIntervalMs);
    },
    [handleResolvedExecutiveMemoJob, stopExecutiveMemoPolling]
  );

  const scheduleNewsJobPoll = useCallback(
    (jobId: string, startedAtMs: number) => {
      stopNewsPolling();
      const pollIntervalMs = getReceiptPollIntervalMs(Math.max(0, Date.now() - startedAtMs));

      newsPollTimerRef.current = window.setTimeout(async () => {
        try {
          const pendingSnapshot = readPendingNewsJobSnapshot();
          const resource = pendingSnapshot?.mode === "report" ? "report" : "news";
          const commonResponse = await fetchCommonAgentJob(resource, jobId);
          if (commonResponse) {
            if (isCommonAgentJobPending(commonResponse)) {
              scheduleNewsJobPoll(jobId, startedAtMs);
              return;
            }

            const notification = toPendingNewsNotificationFromAgentJob(
              commonResponse,
              pendingSnapshot?.mode,
              pendingSnapshot?.keyword
            );
            if (notification) {
              handleResolvedNewsAgentJob(notification);
              return;
            }
          }

          const response = await api.getNewsJob(jobId);

          if (response.status === "accepted") {
            scheduleNewsJobPoll(jobId, startedAtMs);
            return;
          }

          handleResolvedNewsJob(response);
        } catch (error) {
          if (isJobNotFoundError(error)) {
            clearPendingNewsJobSnapshot();
            stopNewsPolling();
            return;
          }

          logError(error);
          scheduleNewsJobPoll(jobId, startedAtMs);
        }
      }, pollIntervalMs);
    },
    [handleResolvedNewsAgentJob, handleResolvedNewsJob, stopNewsPolling]
  );

  const refreshPendingReceiptJob = useCallback(async () => {
    const pendingSnapshot = readPendingReceiptJobSnapshot();
    if (!pendingSnapshot?.jobId) {
      stopReceiptPolling();
      return;
    }

    const startedAtMs = Date.parse(pendingSnapshot.startedAt);
    const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

    stopReceiptPolling();

    try {
      const commonResponse = await fetchCommonAgentJob("receipt", pendingSnapshot.jobId);
      if (commonResponse) {
        if (isCommonAgentJobPending(commonResponse)) {
          scheduleReceiptJobPoll(
            pendingSnapshot.jobId,
            safeStartedAtMs,
            pendingSnapshot.clientRequestId
          );
          return;
        }

        const notification = toPendingReceiptNotificationFromAgentJob(commonResponse);
        if (notification) {
          handleResolvedReceiptAgentJob(notification);
          return;
        }
      }

      const response = await api.getReceiptJob(pendingSnapshot.jobId);

      if (response.status === "accepted") {
        scheduleReceiptJobPoll(
          pendingSnapshot.jobId,
          safeStartedAtMs,
          response.clientRequestId ?? pendingSnapshot.clientRequestId
        );
        return;
      }

      handleResolvedReceiptJob(response);
    } catch (error) {
      if (isJobNotFoundError(error)) {
        clearPendingReceiptJobSnapshot();
        stopReceiptPolling();
        return;
      }

      logError(error);
      scheduleReceiptJobPoll(
        pendingSnapshot.jobId,
        safeStartedAtMs,
        pendingSnapshot.clientRequestId
      );
    }
  }, [
    handleResolvedReceiptAgentJob,
    handleResolvedReceiptJob,
    scheduleReceiptJobPoll,
    stopReceiptPolling,
  ]);

  const refreshPendingExecutiveMemoJob = useCallback(async () => {
    const pendingSnapshot = readPendingExecutiveMemoJobSnapshot();
    if (!pendingSnapshot?.jobId) {
      stopExecutiveMemoPolling();
      return;
    }

    const startedAtMs = Date.parse(pendingSnapshot.startedAt);
    const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

    stopExecutiveMemoPolling();

    try {
      const response = await api.getExecutiveMemoJob(pendingSnapshot.jobId);

      if (response.relayStatus === "accepted" || response.relayStatus === "processing") {
        scheduleExecutiveMemoJobPoll(
          pendingSnapshot.jobId,
          safeStartedAtMs,
          response.clientRequestId ?? pendingSnapshot.clientRequestId
        );
        return;
      }

      handleResolvedExecutiveMemoJob(response);
    } catch (error) {
      if (isJobNotFoundError(error)) {
        clearPendingExecutiveMemoJobSnapshot();
        stopExecutiveMemoPolling();
        return;
      }

      logError(error);
      scheduleExecutiveMemoJobPoll(
        pendingSnapshot.jobId,
        safeStartedAtMs,
        pendingSnapshot.clientRequestId
      );
    }
  }, [
    handleResolvedExecutiveMemoJob,
    scheduleExecutiveMemoJobPoll,
    stopExecutiveMemoPolling,
  ]);

  const refreshPendingNewsJob = useCallback(async () => {
    const pendingSnapshot = readPendingNewsJobSnapshot();
    if (!pendingSnapshot?.jobId) {
      stopNewsPolling();
      return;
    }

    const startedAtMs = Date.parse(pendingSnapshot.startedAt);
    const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

    stopNewsPolling();

    try {
      const resource = pendingSnapshot.mode === "report" ? "report" : "news";
      const commonResponse = await fetchCommonAgentJob(resource, pendingSnapshot.jobId);
      if (commonResponse) {
        if (isCommonAgentJobPending(commonResponse)) {
          scheduleNewsJobPoll(pendingSnapshot.jobId, safeStartedAtMs);
          return;
        }

        const notification = toPendingNewsNotificationFromAgentJob(
          commonResponse,
          pendingSnapshot.mode,
          pendingSnapshot.keyword
        );
        if (notification) {
          handleResolvedNewsAgentJob(notification);
          return;
        }
      }

      const response = await api.getNewsJob(pendingSnapshot.jobId);

      if (response.status === "accepted") {
        scheduleNewsJobPoll(pendingSnapshot.jobId, safeStartedAtMs);
        return;
      }

      handleResolvedNewsJob(response);
    } catch (error) {
      if (isJobNotFoundError(error)) {
        clearPendingNewsJobSnapshot();
        stopNewsPolling();
        return;
      }

      logError(error);
      scheduleNewsJobPoll(pendingSnapshot.jobId, safeStartedAtMs);
    }
  }, [handleResolvedNewsAgentJob, handleResolvedNewsJob, scheduleNewsJobPoll, stopNewsPolling]);

  useEffect(() => {
    syncNotifications();
  }, [syncNotifications]);

  useEffect(() => {
    if (dismissTimerRef.current !== null) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }

    if (!notifications.length) {
      return;
    }

    dismissTimerRef.current = window.setTimeout(() => {
      const [firstNotification] = notifications;
      if (firstNotification) {
        dismissToast(firstNotification);
      }
    }, 5000);

    return () => {
      if (dismissTimerRef.current !== null) {
        window.clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, [dismissToast, notifications]);

  useEffect(() => {
    const handleNotificationEvent = () => {
      syncNotifications();
    };

    window.addEventListener(JOB_NOTIFICATION_EVENT, handleNotificationEvent as EventListener);

    return () => {
      window.removeEventListener(JOB_NOTIFICATION_EVENT, handleNotificationEvent as EventListener);
    };
  }, [syncNotifications]);

  useEffect(() => {
    if (location.pathname === "/receipt") {
      stopReceiptPolling();
    } else {
      void refreshPendingReceiptJob();
    }

    if (location.pathname === "/request/executive-memos") {
      stopExecutiveMemoPolling();
    } else {
      void refreshPendingExecutiveMemoJob();
    }

    void refreshPendingNewsJob();

    const handleFocus = () => {
      if (location.pathname !== "/receipt") {
        void refreshPendingReceiptJob();
      }
      if (location.pathname !== "/request/executive-memos") {
        void refreshPendingExecutiveMemoJob();
      }
      void refreshPendingNewsJob();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopReceiptPolling();
      stopExecutiveMemoPolling();
      stopNewsPolling();
      if (dismissTimerRef.current !== null) {
        window.clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, [
    location.pathname,
    refreshPendingExecutiveMemoJob,
    refreshPendingNewsJob,
    refreshPendingReceiptJob,
    stopExecutiveMemoPolling,
    stopNewsPolling,
    stopReceiptPolling,
  ]);

  if (!notifications.length) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-50 flex justify-center px-4 sm:justify-end sm:px-6">
      <div className="flex w-full max-w-md flex-col gap-3">
        {notifications.map((notification) => {
          const toast = buildJobToast(notification);

          return (
            <div
              key={toast.key}
              className={`pointer-events-auto rounded-[1.4rem] border px-4 py-3 shadow-[0_18px_40px_rgba(74,62,57,0.16)] ${
                toast.tone === "success"
                  ? "border-[#D8E7D3] bg-[#F7FBF3] text-[#4B6650]"
                  : "border-[#E8B2A8] bg-[#FFF3F1] text-[#9A4B40]"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-black">{toast.title}</p>
                  {toast.detail ? <p className="mt-1 text-sm leading-6">{toast.detail}</p> : null}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  {toast.linkTo ? (
                    <Link
                      to={toast.linkTo}
                      onClick={() => dismissToast(notification)}
                      className="rounded-full border border-current/20 bg-white/70 px-3 py-1 text-xs font-bold transition hover:bg-white"
                    >
                      確認する →
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => dismissToast(notification)}
                      className="rounded-full border border-current/20 bg-white/70 px-3 py-1 text-xs font-bold transition hover:bg-white"
                    >
                      確認
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
